import { UserCheck } from "lucide-react";
import {
  LGoButton,
  LGoCard,
  LGoCardContent,
  LGoCardDescription,
  LGoCardHeader,
  LGoCardTitle,
} from "@/ui/branded";
import { CustomFieldAnswers } from "@/features/custom-fields/components/CustomFieldAnswers";
import { canRecordArrival } from "@/domain/reservation/rules";
import type { ReservationLookup, ReservationLookupState } from "@/domain/reservation/types";
import { formatDate, formatDateTime, formatTime } from "@/lib/time";
import { formatPrice } from "@/lib/money";
import { ValidationAnswer } from "./ValidationAnswer";

interface ReservationSummaryProps {
  reservation: ReservationLookup;
  onRecordArrival: () => void;
  isRecording: boolean;
  justRecorded: boolean;
  problem: string | null;
}

export function ReservationSummary({
  reservation,
  onRecordArrival,
  isRecording,
  justRecorded,
  problem,
}: ReservationSummaryProps) {
  const arrivalRecordedAt = reservation.validatedAt;

  if (!canRecordArrival(reservation)) {
    return <ArrivalNotRecordable reservation={reservation} />;
  }

  return (
    <div className="space-y-4">
      {arrivalRecordedAt !== null &&
        (justRecorded ? (
          <ValidationAnswer tone="recorded" headline="Příchod zaznamenán">
            <p>Zaznamenáno v {formatTime(arrivalRecordedAt)}.</p>
          </ValidationAnswer>
        ) : (
          <ValidationAnswer tone="recordedEarlier" headline="Už zaznamenáno">
            <p>
              Příchod k této rezervaci byl zaznamenán {formatDate(arrivalRecordedAt)} v{" "}
              {formatTime(arrivalRecordedAt)}.
            </p>
          </ValidationAnswer>
        ))}

      <LGoCard>
        <LGoCardHeader>
          <LGoCardTitle>{reservation.activityName}</LGoCardTitle>
          <LGoCardDescription>
            {reservation.customerName ?? "Zákazník bez jména"}
          </LGoCardDescription>
        </LGoCardHeader>
        <LGoCardContent className="space-y-6">
          <div className="space-y-2">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Termín</dt>
              <dd className="text-foreground">
                {reservation.startTime ? formatDateTime(reservation.startTime) : "Neuveden"}
              </dd>
              <dt className="text-muted-foreground">Zaplaceno</dt>
              <dd className="text-foreground">{formatPrice(reservation.amountMinor)}</dd>
            </dl>
            <p className="text-sm text-muted-foreground">
              Rezervace je zaplacená předem — nic od zákazníka nevybírejte. Záznam příchodu je jen
              údaj pro vás.
            </p>
          </div>

          {reservation.customAnswers.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Doplňující údaje</h3>
              <CustomFieldAnswers answers={reservation.customAnswers} />
            </section>
          )}

          {arrivalRecordedAt === null && (
            <LGoButton
              size="lg"
              iconLeft={<UserCheck />}
              onClick={onRecordArrival}
              loading={isRecording}
              className="w-full sm:w-auto"
            >
              Zaznamenat příchod
            </LGoButton>
          )}

          {problem && <p className="text-sm text-destructive">{problem}</p>}
        </LGoCardContent>
      </LGoCard>
    </div>
  );
}

function answerWhyArrivalIsNotRecordable(state: ReservationLookupState) {
  switch (state) {
    case "cancelled":
      return {
        tone: "cancelled",
        headline: "Rezervace je zrušená",
        advice: "Příchod nezaznamenávejte.",
      } as const;
    case "pending_payment":
    case "pending_confirmation":
      return {
        tone: "notConfirmed",
        headline: "Rezervace zatím není potvrzená",
        advice: "Příchod zatím nezaznamenávejte.",
      } as const;
    default:
      return {
        tone: "notConfirmed",
        headline: "Rezervace není zaplacená",
        advice: "Příchod nezaznamenávejte.",
      } as const;
  }
}

function ArrivalNotRecordable({ reservation }: { reservation: ReservationLookup }) {
  const { tone, headline, advice } = answerWhyArrivalIsNotRecordable(reservation.state);

  return (
    <div className="space-y-4">
      <ValidationAnswer tone={tone} headline={headline}>
        <p>{advice}</p>
      </ValidationAnswer>

      <LGoCard>
        <LGoCardHeader>
          <LGoCardTitle>{reservation.activityName}</LGoCardTitle>
          <LGoCardDescription>
            {reservation.customerName ?? "Zákazník bez jména"}
          </LGoCardDescription>
        </LGoCardHeader>
        <LGoCardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Termín</dt>
            <dd className="text-foreground">
              {reservation.startTime ? formatDateTime(reservation.startTime) : "Neuveden"}
            </dd>
          </dl>
        </LGoCardContent>
      </LGoCard>
    </div>
  );
}
